/**
 * Block-reference layer chain — the ONE truth for "is this renderable hidden
 * by a layer" once a renderable can be a block child (Codex PR #784 round 3
 * P1 — "satellite visibility = parent INSERT layer AND child layer").
 *
 * # AutoCAD rule
 *
 * A block reference (INSERT) is hidden as a WHOLE when ITS OWN layer is off
 * or frozen — globally (LAYER off / freeze) and per viewport (VPLAYER) —
 * regardless of the layers its children were authored on. Independently,
 * each child is still governed by its own layer (a child on layer B hides
 * when B is off even though the INSERT's layer A is on). So a child draws
 * only when NEITHER its own layer NOR any enclosing reference's layer is
 * hidden: a conjunction over the layer CHAIN.
 *
 * # Representation
 *
 * A block child carries `RenderableEntity.renderParentLayers` — the layers of
 * the enclosing block references (stamped by `scene/liftBlockInsert.ts`, the
 * single render call site of the flatten, so the legacy flat push and the
 * cache-direct satellites get the SAME stamp). The visibility paths that draw
 * or pick a flattened child consume the chain through the helpers here (the
 * one path that does NOT yet is listed under "Residual" below):
 *
 *   - batched line buckets — the chain is part of the bucket `MeshKey`
 *     (`batchedLayer.ts` `parentLayers`), so a child on B under an INSERT on
 *     A lands in its own `(B, parents=[A])` bucket instead of sharing B's
 *     top-level bucket. Hiding A is then a plain per-bucket toggle
 *     (`state/layerVisibility.ts`, the FrameLoops relift handle path and the
 *     per-viewport mask all read `parentLayers` off the handle / mesh
 *     userData) — O(#buckets), no per-slot zero-fill, no GPU re-upload, and
 *     the same mechanism the per-layer toggle already uses. A child whose
 *     chain only repeats its own layer (a layer-"0" child re-homed onto the
 *     INSERT's layer) keeps the plain key — hiding that layer already hides
 *     it, so no extra bucket is minted.
 *   - direct meshes (text / hatch / wipeout / arrow decorators) — the
 *     `EntityRender.parentLayers` field + the `userData.parentLayers` stamp
 *     (`directMeshLayerStamp`), read by `scene/entityRenderVisibility.ts`
 *     (global layer effect + LOD cull) and `rendering/viewportRenderPass.ts
 *     isDirectMeshOnFrozenLayer` (VPLAYER).
 *   - hit-test pre-filters (`filterHittableEntities`, the VP-frozen pick
 *     filter, the window / crossing drag gate) and the grips overlay's
 *     locked gate (`grips/gripsOverlay.tsx isGripLockedByLayerChain`) — a
 *     child under a hidden / locked reference is neither pickable nor
 *     gripped.
 *
 * # Nesting
 *
 * The chain holds EVERY enclosing reference layer, outermost first: for an
 * outer INSERT on A containing a nested INSERT on C whose explicit-layer child
 * sits on B, that child's chain is `[A, C]` and it hides when A, C or B is off
 * / frozen. The nested part is collected during the typed flatten and handed
 * to the stamp out-of-band (`BlockChildAncestry`,
 * `adapters/typed/blockFromEntity.ts`), so the flatten's parity-pinned
 * renderables are unchanged (`scene/liftBlockInsert.ts` module doc).
 *
 * # Residual — NOT parity, a follow-up
 *
 * One path does not apply the conjunction yet, tracked as a follow-up in the
 * ACDb comparison audit,
 * `docs/architecture/2026-09-05_acdb-comparison-and-delivery-audit.md` §11
 * (branch `claude/core-storage-acdb-comparison-5mrjol`): the INSTANCED block
 * path (`adapters/blockInsertInstanced.ts`, one `InstancedMesh` per repeated
 * block, `userData.instanceLayers`) keys per-instance visibility on the
 * INSERT's layer ONLY, so a child's own explicit layer never hides it there —
 * the inverse gap of this module's rule.
 */

import type { RenderableEntity } from "./types";

/**
 * `true` when `layer` or ANY layer of `parentLayers` is in `hidden` (the
 * conjunction "own layer on AND every enclosing reference's layer on",
 * negated). An undefined `layer` contributes nothing (an `EntityRender` with
 * no `entityLayer` is layer-visible on its own).
 */
export function isHiddenByLayerChain(
  layer: string | undefined,
  parentLayers: readonly string[] | undefined,
  hidden: ReadonlySet<string>,
): boolean {
  if (layer !== undefined && hidden.has(layer)) return true;
  if (parentLayers) {
    for (const parent of parentLayers) if (hidden.has(parent)) return true;
  }
  return false;
}

/**
 * `true` when `layer` AND every layer of `parentLayers` is in `present` — the
 * gate a bucket must pass to draw inside a paper-space viewport: its own
 * layer AND each enclosing reference's layer must have a VP-visible entity
 * (`computeVisibleLayers`). A bucket keyed `(B, parents=[A])` holds ONLY
 * children of INSERTs on A, so with no visible entity on A nothing in it can
 * be visible.
 */
export function isLayerChainPresent(
  layer: string,
  parentLayers: readonly string[] | undefined,
  present: ReadonlySet<string>,
): boolean {
  if (!present.has(layer)) return false;
  if (parentLayers) {
    for (const parent of parentLayers) if (!present.has(parent)) return false;
  }
  return true;
}

/**
 * The bucket-key projection of a renderable's chain: the enclosing
 * reference layers that DIFFER from the renderable's own layer, deduplicated
 * and sorted (order-independent key), or `undefined` when none — so a
 * top-level entity and a child whose chain only repeats its own layer share
 * the plain `(layer, colour, linetype)` bucket exactly as before.
 */
export function bucketParentLayers(
  r: Pick<RenderableEntity, "layer" | "renderParentLayers">,
): readonly string[] | undefined {
  const chain = r.renderParentLayers;
  if (!chain || chain.length === 0) return undefined;
  const distinct = new Set<string>();
  for (const parent of chain) if (parent !== r.layer) distinct.add(parent);
  if (distinct.size === 0) return undefined;
  return [...distinct].sort();
}

/**
 * Add `r`'s own layer AND every enclosing reference layer to `out` — the
 * projection the viewport layer sets use (`computeVisibleLayers`, the
 * model/paper census), so a `(B, parents=[A])` bucket's gate can find A even
 * when the INSERT itself is not a spatial-index entity (legacy branch: the
 * children are the index entries, the INSERT is not).
 */
export function addLayerChain(
  r: Pick<RenderableEntity, "layer" | "renderParentLayers">,
  out: Set<string>,
): void {
  out.add(r.layer);
  if (r.renderParentLayers) for (const parent of r.renderParentLayers) out.add(parent);
}

/**
 * Predicate deciding whether ONE render-only child of a `block-insert` wrapper
 * contributes its geometry to the INSERT's own selection tests — see
 * {@link makeVisibleChildGeometryFilter}.
 */
export type ChildGeometryFilter = (
  child: Pick<RenderableEntity, "layer" | "renderParentLayers">,
) => boolean;

/**
 * Build the "is this child currently DRAWN" predicate the INSERT hit-tests use
 * (Codex PR #784 round 3 P2 — "hidden children must not participate in INSERT
 * selection geometry"), or `null` when nothing is hidden (the caller then skips
 * the per-child check entirely — the common case).
 *
 * On the cache-direct branch an INSERT is ONE spatial-index entity whose
 * `childRenderables` are the drawn strokes, so both selection tests measure
 * against the children: the click distance (`raycast/distance.ts`) and the
 * window / crossing predicate (`raycast/windowSelect.ts`). A child hidden by
 * its own layer chain draws NOTHING, so it must neither make the INSERT
 * pickable where nothing is visible (a crossing band over an invisible child)
 * nor VETO a WINDOW selection by lying outside the rubber band. The truth is
 * exactly {@link isHiddenByLayerChain} against the same two sets the render
 * path uses: the GLOBAL off / frozen layers and, inside a paper-space
 * viewport, that VP's frozen set (VPLAYER).
 *
 * LOCKED layers are deliberately NOT part of this predicate. AutoCAD's layer
 * lock gates selection of the OBJECT on that layer, and the object here is the
 * INSERT — governed by its OWN layer, already filtered by
 * `filterHittableEntities` / the drag gate. A child on a locked layer is still
 * DRAWN and is still part of the reference's geometry, so clicking that stroke
 * must still select the (unlocked) block reference.
 */
export function makeVisibleChildGeometryFilter(
  hiddenLayers: ReadonlySet<string>,
  vpFrozenLayers: ReadonlySet<string> | null,
): ChildGeometryFilter | null {
  const hidden = hiddenLayers.size > 0 ? hiddenLayers : null;
  const frozen =
    vpFrozenLayers !== null && vpFrozenLayers.size > 0 ? vpFrozenLayers : null;
  if (hidden === null && frozen === null) return null;
  return (child) => {
    if (
      hidden !== null &&
      isHiddenByLayerChain(child.layer, child.renderParentLayers, hidden)
    ) {
      return false;
    }
    return !(
      frozen !== null &&
      isHiddenByLayerChain(child.layer, child.renderParentLayers, frozen)
    );
  };
}

/** The layer fields a direct mesh stamps into `userData` (see module doc). */
export interface DirectMeshLayerStamp {
  /** The renderable's OWN layer (a satellite child's, never the parent's). */
  entityLayer: string;
  /** The enclosing block-reference layers; absent for a top-level entity. */
  parentLayers?: readonly string[];
}

/**
 * The `userData` layer stamp of a direct mesh built from `r` — spread into
 * the factory's `userData` so every text / hatch / wipeout / arrow / array
 * mesh carries the SAME two keys `isDirectMeshOnFrozenLayer` reads. The
 * `parentLayers` key is omitted (not `undefined`) when there is no chain.
 */
export function directMeshLayerStamp(
  r: Pick<RenderableEntity, "layer" | "renderParentLayers">,
): DirectMeshLayerStamp {
  return r.renderParentLayers
    ? { entityLayer: r.layer, parentLayers: r.renderParentLayers }
    : { entityLayer: r.layer };
}
